import * as signalR from "@microsoft/signalr";
import { toast } from "react-toastify";
import { baseURL } from "../config";

// Fix double slash issue by ensuring proper URL construction
const baseUrl = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL;
const URL = process.env.HUB_ADDRESS ?? baseUrl + "/trade";

class Connector {
    connection;
    events;
    ticks;
    symbols;
    positions;
    executions;
    positionsList;
    static instance;
    keepAliveInterval = null;
    connectionHealthCheck = null;
    lastPingTime = null;
    reconnectAttempts = 0;
    maxReconnectAttempts = 10;
    connectionLock = false; // CRITICAL FIX: Prevent concurrent connection attempts
    lastConnectAttempt = 0; // TASK 1.4: Track last connection attempt timestamp for rate limiting

    //TODO Remove comments in constructor
    constructor(token) {

        this.connection = new signalR.HubConnectionBuilder()
            .withUrl(URL, {
                accessTokenFactory: () => token,
                // CRITICAL FIX: Optimized configuration for minimal requests
                // 1. skipNegotiation: false - ENABLES /negotiate endpoint (you'll see it!)
                // 2. WebSockets | LongPolling - Tries WebSocket first, LongPolling fallback if needed
                // 3. ServerTimeout/KeepAlive optimized to reduce LongPolling frequency
                skipNegotiation: false,
                transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling
            })
            // CRITICAL FIX: Enable automatic reconnection with progressive backoff
            .withAutomaticReconnect([0, 2000, 5000, 10000, 30000]) // 0s, 2s, 5s, 10s, 30s
            .configureLogging(signalR.LogLevel.Information)
            // CRITICAL FIX: INCREASED timeouts to reduce LongPolling frequency if WebSocket fails
            .withServerTimeout(60000)        // 60 seconds (was 30s) - reduces polling
            .withKeepAliveInterval(30000)    // 30 seconds (was 15s) - reduces polling
            .build();

        
        // Set up connection event handlers
        this.setupConnectionHandlers();

        // CRITICAL FIX: Don't auto-start connection in constructor
        // Let useSignalR call connect() method which has rate limiting
        // This prevents duplicate connection attempts and excessive requests

        this.events = (onMessageReceived) => {
        };
    }
    
    setupConnectionHandlers = () => {
        this.connection.on("Connected", (data) => {
        });

        this.connection.on("Pong",(data)=>{
            this.lastPingTime = Date.now(); // Update last ping time when pong is received
            this.events(data);
        });

        this.connection.on("positions",(data)=>{
        });

        // NEW: Handle AccountSwitched event from backend
        this.connection.on("AccountSwitched", (accountNumber) => {
            console.log(`✅ Successfully switched to account ${accountNumber}`);
            this.events({ type: "AccountSwitched", accountNumber });
        });

        // NEW: Handle AccountSwitchFailed event from backend
        this.connection.on("AccountSwitchFailed", (reason) => {
            console.error(`❌ Account switch failed: ${reason}`);
            toast.error(`Failed to switch account: ${reason}`, {
                position: "top-right",
                autoClose: 5000
            });
            this.events({ type: "AccountSwitchFailed", reason });
        });

        // NEW: Handle Disconnected event with reason from backend
        this.connection.on("Disconnected", (reason) => {
            console.warn(`⚠️ Disconnected: ${reason}`);
            this.events({ type: "Disconnected", reason });
        });

        // Handle reconnection events
        this.connection.onreconnecting((error) => {
            console.warn("🔄 SignalR: Connection lost. Reconnecting...", error);
            this.stopKeepAlive();
            this.stopHealthCheck();
        });

        this.connection.onreconnected((connectionId) => {
            this.reconnectAttempts = 0; // Reset counter on successful reconnection
            // Re-authenticate after reconnection
            if (this.connection.state === "Connected") {
                // Token will be automatically sent via accessTokenFactory
                this.startKeepAlive();
                this.startHealthCheck();
            }
        });

        this.connection.onclose((error) => {
            console.error("❌ SignalR: Connection closed.", error);
            this.stopKeepAlive();
            this.stopHealthCheck();
            if (error) {
                this.handleConnectionError(error);
            }
        });
    }
    
    // Start keepalive mechanism to prevent connection timeout
    startKeepAlive = () => {
        if (this.keepAliveInterval) {
            clearInterval(this.keepAliveInterval);
        }
        
        this.keepAliveInterval = setInterval(() => {
            if (this.connection.state === "Connected") {
                this.connection.send("Ping")
                    .then(() => {
                        // Don't update lastPingTime here - wait for pong response
                    })
                    .catch(err => {
                        console.error("Keepalive ping failed:", err);
                        this.handleConnectionError(err);
                    });
            }
        }, 60000); // Send ping every 60 seconds instead of 30
    }
    
    // Stop keepalive mechanism
    stopKeepAlive = () => {
        if (this.keepAliveInterval) {
            clearInterval(this.keepAliveInterval);
            this.keepAliveInterval = null;
        }
    }
    
    // Start health check to monitor connection quality
    startHealthCheck = () => {
        if (this.connectionHealthCheck) {
            clearInterval(this.connectionHealthCheck);
        }
        
        this.connectionHealthCheck = setInterval(() => {
            if (this.connection.state === "Connected") {
                const now = Date.now();
                const timeSinceLastPong = this.lastPingTime ? (now - this.lastPingTime) : null;
                
                
                // Only check if we have a lastPingTime (meaning we've sent at least one ping)
                // and if it's been more than 3 minutes since the last pong response
                if (this.lastPingTime && timeSinceLastPong > 180000) { // 3 minutes
                    console.warn(`No pong response for ${Math.round(timeSinceLastPong/1000)}s, connection may be stale`);
                    this.handleConnectionError(new Error("Connection stale - no pong response"));
                }
            }
        }, 120000); // Check every 2 minutes instead of every minute
    }
    
    // Stop health check
    stopHealthCheck = () => {
        if (this.connectionHealthCheck) {
            clearInterval(this.connectionHealthCheck);
            this.connectionHealthCheck = null;
        }
    }
    
    // CRITICAL FIX: Simplified error handler - let withAutomaticReconnect handle reconnection
    handleConnectionError = (error) => {
        console.error("❌ Connection error:", error);
        this.reconnectAttempts++;

        // Only log errors, don't manually reconnect
        // withAutomaticReconnect will handle reconnection automatically
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error("⚠️ Max reconnection attempts reached");
            toast.error("Connection lost. Please refresh the page to reconnect.");
            return;
        }

    }    
    disconnect = (_token) => {
        this.stopKeepAlive();
        this.stopHealthCheck();
        this.reconnectAttempts = 0;
        
        if (this.connection.state === "Connected"){
            this.connection.invoke("Disconnect", _token)
            .then(()=>{
                this.connection.stop()
                .then(()=>{
                }).catch(err => console.error("Error disconnecting:", err));
            })
            .catch((err)=>console.error("Error sending disconnect ",err));
        }
    }
    connect = (token, accountNumber = null) => {

        // TASK 1.4: Rate limiting to prevent connection storms
        const now = Date.now();
        const timeSinceLastAttempt = now - this.lastConnectAttempt;

        if (this.lastConnectAttempt > 0 && timeSinceLastAttempt < 5000) {
            return;
        }

        this.lastConnectAttempt = now;

        if (!token) {
            console.error("No token provided for SignalR connection");
            return;
        }

        // CRITICAL FIX: Prevent concurrent connection attempts with lock
        if (this.connectionLock) {
            return;
        }

        // If already connected, just send ConnectLps
        if(this.connection.state === "Connected") {
            this.sendConnectLps(token, accountNumber);
            this.startKeepAlive();
            this.startHealthCheck();
            return;
        }

        // If already connecting, skip
        if(this.connection.state === "Connecting") {
            return;
        }

        // Start new connection with lock
        if(this.connection.state === "Disconnected"){
            this.connectionLock = true;

            this.connection.start()
            .then(()=>{
                this.connectionLock = false;

                // Detect transport type
                const connection = this.connection;
                const transport = connection.connection?.transport || connection._transport || null;
                const transportUrl = transport?._url || transport?.url || "N/A";

                let actualTransportType = "Unknown";

                if (transport) {
                    // Check for WebSocket properties
                    if (transport._webSocket || transport.webSocket || transportUrl.startsWith('ws://') || transportUrl.startsWith('wss://')) {
                        actualTransportType = "WebSocket";
                    }
                    // Check for LongPolling properties
                    else if (transport._pollAbort || transport.pollAborted || transportUrl.includes('?id=')) {
                        actualTransportType = "LongPolling";
                    }
                }


                this.sendConnectLps(token, accountNumber);
                this.startKeepAlive();
                this.startHealthCheck();
            }).catch(err => {
                console.error("❌ Error connecting to the hub:", err);
                console.error("❌ Error type:", err.constructor.name);
                console.error("❌ Error message:", err.message);
                console.error("❌ Error stack:", err.stack);

                // DETAILED ERROR ANALYSIS
                if (err.message?.includes('WebSocket')) {
                    console.error("🔴 WEBSOCKET FAILURE - Backend doesn't support WebSocket");
                   
                }
                if (err.message?.includes('negotiate')) {
                    console.error("🔴 NEGOTIATION FAILURE");
                    
                }
                if (err.message?.includes('CORS') || err.message?.includes('Access-Control')) {
                    
                }
                if (err.message?.includes('401') || err.message?.includes('Unauthorized')) {
                    console.error("🔴 AUTHENTICATION ERROR - Token invalid or missing");
                }
                if (err.message?.includes('404')) {
                    console.error("🔴 NOT FOUND - SignalR endpoint doesn't exist");
                    console.error("   Trying to connect to:", URL);
                }

                this.connectionLock = false;
                // Let withAutomaticReconnect handle reconnection
                // Don't manually retry to prevent loops
            });
        }
    }
    
    sendConnectLps = (token, accountNumber) => {
        // Backend requires BOTH token AND accountNumber for account-based filtering
        this.connection.send("ConnectLps", token, accountNumber)
        .then(() => {
            console.log("✅ ConnectLps sent with account:", accountNumber);
        })
        .catch(err => {
            console.error("❌ Error sending ConnectLps:", err);
            // CRITICAL FIX: Don't retry - let connection error handling manage it
            // Retrying here can cause loops
        });
    }

    // NEW: Backend method to switch accounts with invoke (returns promise)
    switchAccount = (accountNumber) => {
        if (!this.connection || this.connection.state !== "Connected") {
            console.error("❌ Cannot switch account: Not connected");
            return Promise.reject("Not connected");
        }

        console.log("🔄 Switching to account:", accountNumber);
        return this.connection.invoke("SwitchAccount", accountNumber)
            .then(() => {
                console.log("✅ SwitchAccount invoke successful for account:", accountNumber);
            })
            .catch(err => {
                console.error("❌ Error invoking SwitchAccount:", err);
                throw err; // Re-throw for caller to handle
            });
    }
    newMessage = (messages) => {
        if(messages === "Disconnect")
            this.connection.send("Disconnect", "zobad")
            .catch(err => console.error("Error sending disconnect request:", err));
        else{    
            this.connection.send(messages).then(()=> {
            }).catch((err)=>{
            });
        }
    };
    sendOrder = (token, order, accountNumber = null)=>{

        if (!token) {
            console.error("SignalR sendOrder: No token provided");
            toast.error("No authentication token");
            return;
        }

        if (this.connection.state !== "Connected") {
            console.error("SignalR sendOrder: Connection not ready, state:", this.connection.state);
            toast.error("Connection not ready. Please wait...");
            return;
        }

        const orderParams = {
            "Type" : order.Type,
            "ClOrdId": ""+order.ClOrdId,
            "SymbolId": parseInt(order.SymbolId),
            "SymbolName": ""+order.SymbolName,
            "LpSymbolName": ""+order.lpSymbolName,
            "TradeSide": order.TradeSide,
            "Quantity": parseFloat(order.Quantity),
            "TargetPrice": order.TargetPrice,
            "Expiry": order.Expiry,
            "PositionId": order.PositionId,
            "Designation": order.Designation,
            "AccountNumber": accountNumber ? parseInt(accountNumber) : null
        };


        this.connection.invoke("SendNewOrderRequest", token, orderParams,accountNumber)
        .then(()=> {
            // No toast here - backend will send ExecutionReport via SignalR
        }).catch((err)=>{
            toast.error("Order request failed: " + (err.message || err), { autoClose: 1000 });
        });
    };
    sendSpreadOrder = (token, order1, order2, accountNumber = null)=>{
        this.connection.invoke("SendSpreadOrderRequest", token,{
            "Type" : order1.Type,
            "ClOrdId": ""+order1.ClOrdId,
            "SymbolId": parseInt(order1.SymbolId),
            "SymbolName": ""+order1.SymbolName,
            "LpSymbolName": ""+order1.lpSymbolName,
            "TradeSide": order1.TradeSide,
            "Quantity": parseFloat(order1.Quantity),
            "TargetPrice": order1.TargetPrice,
            "Expiry": order1.Expiry,
            "PositionId": order1.PositionId,
            "Designation": order1.Designation,
            "AccountNumber": accountNumber ? parseInt(accountNumber) : null
        }, {
            "Type" : order2.Type,
            "ClOrdId": ""+order2.ClOrdId,
            "SymbolId": parseInt(order2.SymbolId),
            "LpSymbolName": ""+order2.lpSymbolName,
            "SymbolName": ""+order2.SymbolName,
            "LpSymbolName": ""+order2.lpSymbolName,
            "TradeSide": order2.TradeSide,
            "Quantity": parseFloat(order2.Quantity),
            "TargetPrice": order2.TargetPrice,
            "Expiry": order2.Expiry,
            "PositionId": order2.PositionId,
            "Designation": order2.Designation,
            "AccountNumber": accountNumber ? parseInt(accountNumber) : null
        }, accountNumber).then(()=> {
            // No toast here - backend will send ExecutionReport via SignalR
        }).catch((err)=>{
            toast.error("Order request failed", { autoClose: 1000 });
        });
    };
    sendCloseOrder = (token, clOrdId, orderId, side = '1') => {
        return this.connection.invoke("SendCloseOrderRequest", token, clOrdId || null, orderId || null, side)
            .then(() => {
                toast.success("Close order request sent");
                return { success: true };
            })
            .catch((err) => {
                console.error("Failed to send close order request:", err);
                toast.error("Failed to send close order request");
                throw err;
            });
    };

    sendClosePosition = (token, params) => {
        return this.connection.invoke("SendClosePositionRequest", token, {
            ClOrdId: params.ClOrdId,
            SymbolId: params.SymbolId,
            SymbolName: params.SymbolName,
            TradeSide: params.TradeSide,
            Quantity: params.Quantity,
            PositionId: params.PositionId,
            Designation: params.Designation
        })
        .then(() => {
            toast.success("Close position request sent");
            return { success: true };
        })
        .catch((err) => {
            console.error("Failed to send close position request:", err);
            toast.error("Failed to send close position request");
            throw err;
        });
    };

    sendOrderAmmend = (token, order)=>{
        this.connection.invoke("SendOrderAmmendRequest", token,{
            "ClOrderId": ""+order.ClOrdId,
            "OrderType" : order.Type,
            "OrderId" : ""+order.OrderId,
            "OrderQty": parseFloat(order.Quantity),
            "TargetPrice": order.TargetPrice,
            "Expiry": order.Expiry            
        }).then(()=> {
            toast.success("Order ammend request sent");         
        }).catch((err)=>{
        });
    };
    sendPositionClose = (id)=>{
        this.connection.invoke("SendCloseOrderRequest", id).then(()=> {
        }).catch((err)=>{
        });
    }
    sendOrderClose = (token, clOrdId, orderId)=>{
        const ClOrderId = ""+clOrdId;
        const OrderId = ""+orderId;
        const param = {
            token : token,
            ClOrderId: ClOrderId,
            OrderId: OrderId
        };
        this.connection.send("SendCloseOrderRequest", token, ClOrderId, OrderId)
        .then(()=> {
            toast.success("Order close request sent");
        }).catch((err)=>{
        });
    }
    getStatus = () => {
        return this.connection.state;
    };
    cleanup = () => {
        this.stopKeepAlive();
        this.stopHealthCheck();
        this.connection.off("Connected");
        this.connection.off("Pong");
        this.connection.off("SymbolQuotes");
        this.reconnectAttempts = 0;
    };
    // connectIfNeeded = () => {
        
    //     if (this.connection.state !== "Connected") {
    //         this.connect(token);
    //     }
    // };
    
}
export const getSignalRInstance= (token)=>  {
    // If no instance exists, create one
    if (!Connector.instance) {
        Connector.instance = new Connector(token);
        return Connector.instance;
    }
    
    // If instance exists but is disconnected, recreate it
    if (Connector.instance.getStatus() === "Disconnected") {
        Connector.instance.cleanup();
        Connector.instance = new Connector(token);
        return Connector.instance;
    }
    
    // If instance exists and is connected or connecting, return it
    if (Connector.instance.getStatus() === "Connected" || Connector.instance.getStatus() === "Connecting") {
        return Connector.instance;
    }
    
    // For any other state, recreate the instance
    Connector.instance.cleanup();
    Connector.instance = new Connector(token);
    return Connector.instance;
}

// Clear instance for logout/cleanup
export const clearSignalRInstance = () => {
    if (Connector.instance) {
        Connector.instance.cleanup();
        Connector.instance.disconnect();
        Connector.instance = null;
    }
};

// Export the Connector class for testing if needed
export { Connector };